import { useEffect } from 'react'

import type { PageSubscription } from './protocol'
import { getRealtimeClient } from './singleton'

/**
 * Joins a page's broadcast group for as long as the calling component is mounted.
 *
 * **Most realtime events go nowhere without this.** The connection carries the
 * events about a workspace, its applications and the signed-in account to every
 * tab that has one open, but everything about the *contents* of a table or a
 * dashboard is broadcast to a named group -- `table-101`, `dashboard-12` -- and
 * the server sends nothing to a socket that has not asked to join. A row created
 * in another tab reaches a handler that is registered, correct and never called.
 *
 * The parameters are spread into the message the server reads, so their names
 * are the page type's own: `table_id` for a table, `dashboard_id` for a
 * dashboard.
 *
 * The client keeps the subscription list itself and replays it on reconnect, so
 * a page joined while the socket is down is joined when it comes back.
 */
export function useRealtimePage(
  page: string,
  parameters: PageSubscription['parameters'],
): void {
  // Serialised so the effect depends on the values rather than the object, which
  // is rebuilt on every render at every call site.
  const key = JSON.stringify(parameters)

  useEffect(() => {
    const client = getRealtimeClient()
    const held = JSON.parse(key) as PageSubscription['parameters']
    client.subscribe(page, held)
    return () => {
      client.unsubscribe(page, held)
    }
  }, [page, key])
}
